import {createStore} from 'vuex'
import { getSavedState, saveState } from "./helpers.js";
import tokenService from "@services/tokenService";
import route from '@router';
import jwt_decode from "jwt-decode";

import ui from './module_ui.js';
import panier_demande from './module_panier_demande.js';
import panier_retour from './module_panier_retour.js';
import permission from './module_permission.js';
import viewable from './module_viewable.js';

export default createStore({
    modules: {
        ui, panier_demande, panier_retour, permission, viewable
    },
    state: {
        currentUser: getSavedState('currentUser'),
        userInformation: getSavedState('userInformation'),
        userParams: getSavedState('userParams'),
    },
    mutations: {
        SET_CURRENT_USER(state, newValue) {
            state.currentUser = newValue;
            saveState('currentUser', newValue);
        },
        SET_USER_INFO(state, newValue) {
            state.userInformation = newValue;
            saveState('userInformation', newValue);
        },
        SET_USER_PARAMS(state, newValue) {
            state.userParams = newValue;
            saveState('userParams', newValue);
        },
        SET_DATE_CHARTE_ACCEPTEE(state, newValue) {
            if (state.userInformation) {
                state.userInformation.dateCharteAcceptee = newValue;
                saveState('userInformation', state.userInformation);
            } else {
                console.warn("Cannot set dateCharteAcceptee, currentUser is not defined.");
            }
        },
        
        UPDATE_FIELD(state, { path, value }) {
          const keys = path.split(".");
          let target = state;
      
          for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!target[key] || typeof target[key] !== "object") target[key] = {};
            target = target[key];
          }
          const lastKey = keys[keys.length - 1];
          target[lastKey] = value;
          saveState(keys[0], state[keys[0]]);
        },
    },
    getters: {
        loggedIn(state) {
            return !!state.currentUser;
        },
        username(state) {
            return state.userInformation?.firstName + " " + state.userInformation?.lastName;
        },
        userId(state) {
            return state.userInformation?.userId;
        },
    },
    actions: {
        // This is automatically run in `src/state/store.js` when the app
        // starts, along with any other actions named `init` in other modules.
        
        init({state, dispatch}) {
            dispatch('validate');
        },
        
        updateField({ commit }, payload) {
          commit("UPDATE_FIELD", payload);
        },

        // Logs in the current user.
        logIn({commit, dispatch, getters}, {username, password} = {}) {
            if (getters.loggedIn) return dispatch('validate');
            
            sessionStorage.removeItem("dt-link-list");
            sessionStorage.removeItem("dt-forecast-list");
            sessionStorage.removeItem("dt-intervention-list");
            sessionStorage.removeItem("dt-circuit-list");
            
            let user = {login: username, password: password};
            
            return tokenService.login(user).then(data => {
                const user = data;
                const decoded = jwt_decode(data.jwt);
                
                const userInfo = {
                    platform: decoded.platform,
                    userId: decoded.userId,
                    firstName: decoded.firstName,
                    lastName: decoded.lastName,
                    email: decoded.email,
                    tel: decoded.tel,
                    pwdReset: decoded.pwdReset,
                    userGroupNames: decoded.userGroupNames,
                    userGroupTechNames: decoded.userGroupTechNames,
                    affectations: decoded.affectations,
                    dateCharteAcceptee: decoded.dateCharteAcceptee,
                };
                
                const userParam = {
                    link: {
                        searchValue: "",
                        typeToShow: "all",
                        perDay: false,
                        date: new Date(),
                        author: "",
                        night: null,
                        sheetType: null,
                        agerIdSelected: null,
                        uerIdSelected: null,
                        ceiIdSelected: null,
                        isShowingAll: false,
                        startDate: null,
                        endDate: null,
                        noFiche: "",
                        showAlerts: 'null',
                    },
                    forecast: {
                        searchValue: "",
                        typeToShow: "all",
                        perDay: false,
                        date: new Date(),
                        author: "",
                        agerIdSelected: null,
                        uerIdSelected: null,
                        ceiIdSelected: null,
                        startDate: null,
                        endDate: null,
                        noFiche: "",
                    },
                    intervention: {
                        searchValue: "",
                        presenceRi: null,
                        ddp: null,
                        callOrigin: null,
                        axeInterventionId: null,
                        sens: null,
                        typeIntervention: null,
                        author: "",
                        reference: null,
                        startDate: null,
                        endDate: null,
                        paginateOptions: {
                            page: 0, rows: 10
                            , sort: "call_date:DESC,call_time:DESC"
                        },
                        noInterCei: "",
                        inciviliteAgression: null
                    },
                    circuit: {
                        ceiId: null,
                        uerId: null,
                        agerId: null,
                    }
                };
                
                const panierDemandeSize = decoded.panierDemandeSize;
                const panierRetourSize = decoded.panierRetourSize;
                const canCreatePreDemande = decoded.canCreatePreDemande;
                const canCreatePreRetour = decoded.canCreatePreRetour;
                
                const permissionList = decoded.permissionList;
                const viewablePages = decoded.viewablePages;
                
                dispatch('panier_demande/setSize', panierDemandeSize);
                dispatch('panier_retour/setSize', panierRetourSize);
                dispatch('panier_demande/setCanCreatePre', canCreatePreDemande);
                dispatch('panier_retour/setCanCreatePre', canCreatePreRetour);
                
                dispatch('permission/setList', permissionList);
                dispatch('viewable/setPages', viewablePages);
                
                commit('SET_CURRENT_USER', user);
                commit('SET_USER_INFO', userInfo);
                commit('SET_USER_PARAMS', userParam);
                
                return user;
            });
        },
        setCurrentUser({commit}, newValue) {
            commit('SET_CURRENT_USER', newValue);
        },
        setDateCharteAcceptee({commit}, newValue) {
            commit('SET_DATE_CHARTE_ACCEPTEE', newValue);
        },
        setUserParams({commit}, userParams) {
            commit('SET_USER_PARAMS', userParams)
        },
        resetLinkUserParams({commit}) {
            sessionStorage.removeItem("dt-link-list");
            let data = getSavedState('userParams');
            data.link = {
                searchValue: "",
                typeToShow: "all",
                perDay: false,
                date: new Date(),
                author: "",
                night: null,
                sheetType: null,
                agerIdSelected: null,
                uerIdSelected: null,
                ceiIdSelected: null,
                isShowingAll: false,
                startDate: null,
                endDate: null,
                noFiche: "",
                showAlerts: 'null',
            };
            commit('SET_USER_PARAMS', data)
        },
        resetForecastUserParams({commit}) {
            sessionStorage.removeItem("dt-forecast-list");
            let data = getSavedState('userParams');
            data.forecast = {
                searchValue: "",
                typeToShow: "all",
                perDay: false,
                date: new Date(),
                author: "",
                agerIdSelected: null,
                uerIdSelected: null,
                ceiIdSelected: null,
                startDate: null,
                endDate: null,
                noFiche: "",
            };
            commit('SET_USER_PARAMS', data)
        },
        resetInterventionUserParams({commit}) {
            sessionStorage.removeItem("dt-intervention-list");
            let data = getSavedState('userParams');
            data.intervention = {
                searchValue: "",
                presenceRi: null,
                ddp: null,
                callOrigin: null,
                axeInterventionId: null,
                sens: null,
                typeIntervention: null,
                author: "",
                reference: null,
                startDate: null,
                endDate: null,
                paginateOptions: {
                    page: 0, rows: 10
                    , sort: "call_date:DESC,call_time:DESC"
                },
                noInterCei: "",
            };
            commit('SET_USER_PARAMS', data)
        },
        resetCircuitUserParams({commit}) {
            let data = getSavedState('userParams');
            data.circuit = {
                ceiId: null,
                uerId: null,
                agerId: null,
            }
            commit('SET_USER_PARAMS', data)
        },

        // Logs out the current user.
        logOut({commit, dispatch}) {
            commit('SET_CURRENT_USER', null);
            commit('SET_USER_INFO', null);
            commit('SET_USER_PARAMS', null);
            dispatch('permission/setList', null);
            route.push({name: "login"});
        },

        logOutExpired({commit, dispatch}) {
            commit('SET_CURRENT_USER', null);
            commit('SET_USER_INFO', null);
            commit('SET_USER_PARAMS', null);
            dispatch('permission/setList', null);
            route.push({name: "login", query: {redirectFrom: route.currentRoute.value.fullPath}});
        },

        // Validates the current user's token and refreshes it
        // with new data from the API.
        validate({commit, state}) {
            if (!state.currentUser) return Promise.resolve(null)
            return state.currentUser;
        },

        checkCharte({commit, state}) {
            if (!state.userInformation) return Promise.resolve(null);
            if (!state.userInformation.dateCharteAcceptee) {
                return Promise.resolve(false);
            } else {
                return Promise.resolve(true);
            }
        }
    },
})